import React from 'react';
import { Timeline, Collapse } from 'antd';
import { DateTime } from 'luxon';
import TimelineStepText from '../../common/containers/TimelineStepText';
import OntraccrButton from '../../common/buttons/OnTraccrButton';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';

const { Panel } = Collapse;

const PROP_MAP = {
  fieldAdded: {
    title: 'Field Added',
    dot: <PlusOutlined />,
  },
  fieldEdited: {
    title: 'Field Edited',
    dot: <EditOutlined />,
  },
  fieldDeleted: {
    title: 'Field Deleted',
    dot: <DeleteOutlined />,
  },
  sectionAdded: {
    title: 'Section Added',
    dot: <PlusOutlined />,
  },
  sectionEdited: {
    title: 'Section Edited',
    dot: <EditOutlined />,
  },
  sectionDeleted: {
    title: 'Section Deleted',
    dot: <DeleteOutlined />,
  },
  workflowStepAdded: {
    title: 'Workflow Step Added',
    dot: <PlusOutlined />,
  },
  workflowStepEdited: {
    title: 'Workflow Step Edited',
    dot: <EditOutlined />,
  },
  workflowStepDeleted: {
    title: 'Workflow Step Deleted',
    dot: <DeleteOutlined />,
  },
  templateRestored: {
    title: 'Template Restored',
    dot: <HistoryOutlined />,
  },
};

const bold = (text) => <span className="form-template-version-bold">{text}</span>;

const ITEM_TYPE_MAP = {
  field: 'field',
  section: 'section',
  workflowStep: 'workflow step',
  closePOWorkflowStep: 'close PO workflow step',
  template: 'template',
};

const ACTION_TEXT_MAP = {
  Added: 'added',
  Edited: 'edited',
  Deleted: 'deleted',
  Restored: 'restored',
};

const getPreviewInitialTab = (itemType) => {
  if (itemType === 'closePOWorkflowStep') return 'closePOWorkflow';
  if (itemType === 'workflowStep') return 'workflow';
  return 'fields';
};

const getVersionNumber = (version) => {
  if (version.isLegacy) {
    return 'Changes before versioning (Version 1)';
  }
  return `Version ${version.versionNumber}`;
};

const getVersionItemSubtitle = (version) => {
  const userName = version.userName ?? 'Unknown User';
  
  if (version.isLegacy) {
    return null;
  }

  if (version.sourceType === 'restore' && version.restoredFromVersionNumber) {
    return (
      <span>
        {bold(userName)} restored from version {bold(version.restoredFromVersionNumber)}
      </span>
    )
  }

  if (version.sourceType === 'create') {
    return (
      <span>
        {bold(userName)} created version {bold(version.versionNumber)}
      </span>
    )
  }

  if (version.sourceType === 'edit') {
    return (
      <span>
        {bold(userName)} edited to version {bold(version.versionNumber)}
      </span>
    )
  }

  return null;
};

const getActionText = (change, isLegacy) => {
  const {
    type,
    itemName,
    itemType,
    userName,
    timestamp,
  } = change ?? {};

  const itemTypeText = ITEM_TYPE_MAP[itemType] || 'item';
  const actionMatch = Object.keys(ACTION_TEXT_MAP).find((key) => type.includes(key));
  const actionText = actionMatch ? ACTION_TEXT_MAP[actionMatch] : 'modified';

  if (isLegacy) {
    const formattedDate = DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_MED);
    return (
      <span>
        {bold(userName ?? 'Unknown User')} {actionText} the {bold(`'${itemName}'`)} {itemTypeText}
        {` - ${formattedDate}`}
      </span>
    );
  }

  return (
    <span>
      {actionText} {bold(`'${itemName}'`)} {itemTypeText}
    </span>
  );
};

function TimelineVersionItem({
  version,
  onPreviewOpen,
}) {

  const canPreview = !version.isLatest;
  const versionChanges = version.changes ?? [];

  // sort by timestamp, then workflow -> sections -> fields
  const sortedVersionChanges = [...versionChanges].sort((a, b) => {
    if (a.timestamp !== b.timestamp) return b.timestamp - a.timestamp;
    if (a.itemType === 'workflowStep' || a.itemType === 'closePOWorkflowStep') return -1;
    if (b.itemType === 'workflowStep' || b.itemType === 'closePOWorkflowStep') return 1;
    if (a.itemType === 'section') return -1;
    if (b.itemType === 'section') return 1;
    return 0;
  });

  const noChangesText = version.isLegacy ?
    <div>No changes recorded.</div> :
    null;

  const handlePreviewOpen = (initialTab = 'fields') => {
    if (version.isLatest) return;
    const validInitialTab = getPreviewInitialTab(initialTab);
    onPreviewOpen({
      versionId: version.versionId,
      versionNumber: version.versionNumber,
      isLatest: version.isLatest,
      isLegacy: version.isLegacy,
      initialTab: validInitialTab,
    });
  };
  
  return (
    <Timeline.Item>
      <TimelineStepText
        title={getVersionNumber(version)}
        timestamp={version.timestamp}
        text={(
          <div>
            <div className="form-template-version-header-text">
              {getVersionItemSubtitle(version)}
            </div>
            {canPreview && (
              <OntraccrButton
                type="primary"
                title="View"
                style={{ marginTop: 8 }}
                onClick={() => handlePreviewOpen()}
              />
            )}
            {sortedVersionChanges.length > 0 ? (
              <Collapse
                ghost={true}
                className="form-template-version-collapse"
              >
                <Panel
                  header={`${sortedVersionChanges.length} changes`}
                  key="changes"
                >
                  {sortedVersionChanges.map((change) => {
                    const propDot = PROP_MAP[change.type]?.dot;
                    return (
                      <button
                        key={change.id}
                        className="form-template-version-change-row"
                        onClick={() => handlePreviewOpen(change.itemType)}
                      >
                        <span className="form-template-version-change-row-icon">{propDot}</span>
                        <span>{getActionText(change, version.isLegacy)}</span>
                      </button>
                    );
                  })}
                </Panel>
              </Collapse>
            ) : (
              noChangesText
            )}
          </div>
        )}
      />
    </Timeline.Item>
  );
}

TimelineVersionItem.propTypes = {
  version: PropTypes.shape({
    versionId: PropTypes.string,
    versionNumber: PropTypes.number,
    timestamp: PropTypes.number,
    userName: PropTypes.string,
    sourceType: PropTypes.string,
    restoredFromVersionNumber: PropTypes.number,
    isLatest: PropTypes.bool,
    isLegacy: PropTypes.bool,
    changes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      type: PropTypes.string,
      itemName: PropTypes.string,
      itemType: PropTypes.string,
      metadata: PropTypes.shape({}),
      userName: PropTypes.string,
      timestamp: PropTypes.number,
    })),
  }).isRequired,
  onPreviewOpen: PropTypes.func.isRequired,
};

export default TimelineVersionItem;
